/**
 * StatsTable - Expanded stats table showing all props for a player
 *
 * Columns: Stat, Projection, Best Line, True Odds, Book Win %, Model Win %, EV %, Other Books
 * Rows: PTS, REB, AST, 3PM, PRA, P+A, P+R, R+A
 */
import React, { useState, memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Stack,
  Typography,
  Button,
  Tooltip,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from 'omaha-design-system';
import { Plus, Minus } from '@phosphor-icons/react';
import { PlayerData, DisplayGame, PlayerProp, PlayerPropSportsbook, BetSlipItem } from '../../data/types';
import { SportsbookLogo } from '../SportsbookLogo';
import { CalculateEVResponse } from '../../data/evTypes';
import { addToBetSlip, removeFromBetSlip } from '../../store/slice';
import { IRootState } from '../../../../store/reducer';
import OtherBooksBottomSheet from '../OtherBooksBottomSheet';
import MobilePropRow from './MobilePropRow';
import { STAT_LABELS, HIDDEN_STATS } from '../../data/statLabels';

// Toggle this to switch between vertical cards (true) and horizontal scroll table (false)
const USE_VERTICAL_MOBILE_LAYOUT = true;

interface StatsTableProps {
  player: PlayerData;
  game: DisplayGame;
  customProjections: {
    pts: number;
    reb: number;
    ast: number;
    threes: number;
    pra: number;
    // NHL stats
    goals: number;
    nhlAssists: number;
    sog: number;
    saves: number;
    nhlPoints: number;
    blocks: number;
    // NFL stats (volume × rate)
    passAtt?: number;
    rushAtt?: number;
    receptions?: number;
    passYds?: number;
    passTds?: number;
    comp?: number;
    ints?: number;
    rushYds?: number;
    rushTds?: number;
    recYds?: number;
    recTds?: number;
    passRushYds?: number;
    rushRecYds?: number;
    anyTd?: number;
    rushRecTds?: number;
  };
  /** Custom EV data from recalculation API */
  customEVData?: CalculateEVResponse | null;
  /** Whether custom EV data is available */
  isCustomEV?: boolean;
  /** Whether EV calculation is in progress */
  isEVLoading?: boolean;
}

// Get custom projection value for a stat
const getCustomProjection = (
  stat: string,
  customProjections: StatsTableProps['customProjections']
): number | null => {
  const normalized = STAT_LABELS[stat] || stat;
  switch (normalized) {
    // NBA stats
    case 'PTS':
      // For NHL, PTS means total points (nhlPoints); for NBA, it's pts
      // NHL players have pts=0, NBA players have nhlPoints=0
      return customProjections.nhlPoints || customProjections.pts;
    case 'REB':
      return customProjections.reb;
    case 'AST':
      // For NHL, AST means total assists (nhlAssists); for NBA, it's ast
      return customProjections.nhlAssists || customProjections.ast;
    case '3PM':
      return customProjections.threes;
    case 'PRA':
      return customProjections.pra;
    case 'P+A':
      return customProjections.pts + customProjections.ast;
    case 'P+R':
      return customProjections.pts + customProjections.reb;
    case 'R+A':
      return customProjections.reb + customProjections.ast;
    // NHL stats
    case 'G':
      return customProjections.goals;
    case 'SOG':
      return customProjections.sog;
    case 'BLK':
      return customProjections.blocks;
    case 'SV':
      return customProjections.saves;
    // NFL stats
    case 'Pass Att':
      return customProjections.passAtt ?? null;
    case 'Rush Att':
      return customProjections.rushAtt ?? null;
    case 'Rec':
      return customProjections.receptions ?? null;
    case 'Pass Yds':
      return customProjections.passYds ?? null;
    case 'Pass TDs':
      return customProjections.passTds ?? null;
    case 'Comp':
      return customProjections.comp ?? null;
    case 'INT':
      return customProjections.ints ?? null;
    case 'Rush Yds':
      return customProjections.rushYds ?? null;
    case 'Rush TDs':
      return customProjections.rushTds ?? null;
    case 'Rec Yds':
      return customProjections.recYds ?? null;
    case 'Rec TDs':
      return customProjections.recTds ?? null;
    case 'Pass+Rush Yds':
      return customProjections.passRushYds ?? null;
    case 'Rush+Rec Yds':
      return customProjections.rushRecYds ?? null;
    case 'Any TD':
      return customProjections.anyTd ?? null;
    case 'Rush+Rec TDs':
      return customProjections.rushRecTds ?? null;
    default:
      return null;
  }
};

// Get custom EV for a specific stat/side/sportsbook from the custom EV response
const getCustomEV = (
  stat: string,
  side: 'Over' | 'Under',
  sportsbookCode: string,
  customEVData: CalculateEVResponse | null | undefined
): number | null => {
  if (!customEVData?.outcomes) return null;

  // The API response uses the same statType we sent (e.g., "Points", "3PM")
  // Try matching both the raw stat and normalized version
  const normalized = STAT_LABELS[stat] || stat;
  const outcome = customEVData.outcomes.find(
    (o) =>
      (o.statType === stat || o.statType === normalized) &&
      o.label === side
  );

  if (!outcome) return null;

  const sbResult = outcome.sportsbooks.find((sb) => sb.code === sportsbookCode);

  // API returns blendedEV as decimal (e.g., 0.051 for 5.1%), convert to percentage
  const ev = sbResult?.blendedEV;
  return ev != null ? ev * 100 : null;
};

// Get custom Model Win % (blended true win probability) from the custom EV response
const getCustomModelWin = (
  stat: string,
  side: 'Over' | 'Under',
  sportsbookCode: string,
  customEVData: CalculateEVResponse | null | undefined
): number | null => {
  if (!customEVData?.outcomes) return null;

  const normalized = STAT_LABELS[stat] || stat;
  const outcome = customEVData.outcomes.find(
    (o) =>
      (o.statType === stat || o.statType === normalized) &&
      o.label === side
  );

  if (!outcome) return null;

  const sbResult = outcome.sportsbooks.find((sb) => sb.code === sportsbookCode);

  // API returns blendedTrueWinProbability as decimal (e.g., 0.55 for 55%)
  const modelWin = sbResult?.blendedTrueWinProbability;
  return modelWin ?? null;
};

interface PropRowProps {
  prop: PlayerProp;
  player: PlayerData;
  customProjection: number | null;
  customEVData?: CalculateEVResponse | null;
  isCustomEV?: boolean;
  isEVLoading?: boolean;
  betSlip: BetSlipItem[];
  onAddToBetSlip: (sportsbook: PlayerPropSportsbook, customValues: {
    projection: number;
    evPct: number | null;
    modelWin: number | null;
  }) => void;
  onRemoveFromBetSlip: (betId: string) => void;
  onOpenOtherBooks?: () => void;
}

const PropRow: React.FC<PropRowProps> = ({
  prop,
  player,
  customProjection,
  customEVData,
  isCustomEV,
  isEVLoading,
  betSlip,
  onAddToBetSlip,
  onRemoveFromBetSlip,
  onOpenOtherBooks,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get the best sportsbook (first one, sorted by EV)
  const bestBook = prop.sportsbooks[0];
  const otherBooks = prop.sportsbooks.slice(1);
  const totalOtherBooks = prop.sportsbooks.length - 1; // All books except the best one

  // Generate bet ID to check if it's in the bet slip
  const betId = bestBook ? `${player.id}-${prop.stat}-${bestBook.book}-${bestBook.side}` : '';
  const isInBetSlip = betId ? betSlip.some((bet) => bet.id === betId) : false;

  if (!bestBook) {
    return null;
  }

  // Use custom projection if available, otherwise use the prop's projection
  const displayProjection = customProjection ?? prop.projection;

  // Recalculate delta based on custom projection
  const delta = displayProjection - bestBook.line;

  // Get custom EV if available, otherwise use original
  const customEV = isCustomEV
    ? getCustomEV(prop.stat, bestBook.side, bestBook.book, customEVData)
    : null;
  const displayEV = customEV ?? bestBook.evPct;

  // Get custom Model Win % if available, otherwise use original
  const customModelWin = isCustomEV
    ? getCustomModelWin(prop.stat, bestBook.side, bestBook.book, customEVData)
    : null;
  const displayModelWin = customModelWin ?? bestBook.modelWin;

  // Mobile layout - scrollable row with all columns matching Figma
  if (isMobile) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          py: 1.5,
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          '&:last-child': {
            borderBottom: 'none',
          },
          minWidth: 'max-content',
        }}
      >
        {/* Stat */}
        <Typography sx={{ fontWeight: 500, color: '#f1f2f2', fontSize: '13.33px', width: 50, flexShrink: 0 }}>
          {STAT_LABELS[prop.stat] || prop.stat}
        </Typography>

        {/* Projection */}
        <Typography sx={{ fontWeight: 500, color: '#f1f2f2', fontSize: '13.33px', width: 70, flexShrink: 0, textAlign: 'center' }}>
          {displayProjection.toFixed(1)}
        </Typography>

        {/* Best Line */}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: 100, flexShrink: 0 }}>
          <Typography sx={{ color: '#f1f2f2', fontSize: '13.33px' }}>
            {bestBook.side === 'Over' ? 'o' : 'u'}
            {bestBook.line.toFixed(1)} {bestBook.americanOdds}
          </Typography>
          <SportsbookLogo book={bestBook.book} bookIcon={bestBook.bookIcon} size={18} borderRadius="3px" />
        </Stack>

        {/* True Odds */}
        <Typography sx={{ color: '#f2f2f3b2', fontSize: '13.33px', width: 60, flexShrink: 0, textAlign: 'center' }}>
          {bestBook.marketWin ? (
            bestBook.marketWin >= 0.5
              ? Math.round(-100 * bestBook.marketWin / (1 - bestBook.marketWin))
              : `+${Math.round(100 * (1 - bestBook.marketWin) / bestBook.marketWin)}`
          ) : '-'}
        </Typography>

        {/* Book Win % */}
        <Typography sx={{ color: '#f2f2f3b2', fontSize: '13.33px', width: 60, flexShrink: 0, textAlign: 'center' }}>
          {bestBook.bookWin ? `${(bestBook.bookWin * 100).toFixed(0)}%` : '-'}
        </Typography>

        {/* Model Win % */}
        <Box
          sx={{
            width: 70,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isEVLoading ? (
            <CircularProgress size={14} sx={{ color: '#42A5F5' }} />
          ) : (
            <Typography sx={{ fontWeight: 500, color: '#66BB6A', fontSize: '13.33px', textAlign: 'center' }}>
              {displayModelWin ? `${(displayModelWin * 100).toFixed(0)}%` : '-'}
            </Typography>
          )}
        </Box>

        {/* EV % */}
        <Box
          sx={{
            width: 60,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isEVLoading ? (
            <CircularProgress size={14} sx={{ color: '#42A5F5' }} />
          ) : (
            <Typography
              sx={{
                fontWeight: 600,
                color: displayEV !== null && displayEV >= 0 ? '#66BB6A' : '#EF5350',
                fontSize: '13.33px',
                textAlign: 'center',
              }}
            >
              {displayEV !== null
                ? `${displayEV >= 0 ? '+' : ''}${displayEV.toFixed(1)}%`
                : '-'}
            </Typography>
          )}
        </Box>

        {/* Other Books - shows "+X books" text like Top Bets mobile */}
        <Box
          onClick={(e) => {
            e.stopPropagation();
            if (totalOtherBooks > 0 && onOpenOtherBooks) {
              onOpenOtherBooks();
            }
          }}
          sx={{
            width: 80,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: totalOtherBooks > 0 ? 'pointer' : 'default',
          }}
        >
          {totalOtherBooks > 0 ? (
            <Typography
              sx={{
                color: '#42A5F5',
                fontSize: '13.33px',
                fontWeight: 500,
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
              }}
            >
              +{totalOtherBooks} {totalOtherBooks === 1 ? 'book' : 'books'}
            </Typography>
          ) : (
            <Typography sx={{ color: '#f2f2f3b2', fontSize: '13.33px' }}>-</Typography>
          )}
        </Box>

        {/* Action Buttons */}
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 1, flexShrink: 0 }}>
          <Button
            variant="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (isInBetSlip) {
                onRemoveFromBetSlip(betId);
              } else {
                onAddToBetSlip(bestBook, {
                  projection: displayProjection,
                  evPct: displayEV,
                  modelWin: displayModelWin,
                });
              }
            }}
            startIcon={isInBetSlip ? <Minus size={14} weight="bold" /> : <Plus size={14} weight="bold" />}
            sx={{
              color: isInBetSlip ? '#EF5350' : '#42A5F5',
              fontSize: '13.33px',
              fontWeight: 600,
              textTransform: 'none',
              px: 1.5,
              py: 0.5,
              width: '88px',
              '&:hover': {
                backgroundColor: isInBetSlip ? 'rgba(239, 83, 80, 0.08)' : 'rgba(66, 165, 245, 0.08)',
              },
            }}
          >
            {isInBetSlip ? 'Remove' : 'Add'}
          </Button>
          <Button
            variant="outlined"
            color="success"
            size="small"
            disabled={!bestBook.deepLinkUrl}
            onClick={(e) => {
              e.stopPropagation();
              if (bestBook.deepLinkUrl) {
                window.open(bestBook.deepLinkUrl, '_blank');
              }
            }}
            sx={{
              fontSize: '13.33px',
              fontWeight: 600,
              textTransform: 'none',
              px: 1.5,
              py: 0.5,
              minWidth: 'auto',
            }}
          >
            Bet
          </Button>
        </Stack>
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '70px 70px 130px 120px 100px 110px 80px 1fr 150px',
        alignItems: 'center',
        py: 1,
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        '&:last-child': {
          borderBottom: 'none',
        },
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
        },
      }}
    >
      {/* Stat */}
      <Typography variant="body2" sx={{ fontWeight: 500, color: '#f1f2f2' }}>
        {STAT_LABELS[prop.stat] || prop.stat}
      </Typography>

      {/* Projection */}
      <Typography variant="body2" sx={{ fontWeight: 500, color: '#f1f2f2', textAlign: 'center' }}>
        {displayProjection.toFixed(1)}
      </Typography>

      {/* Best Line */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" sx={{ color: '#f1f2f2' }}>
          {bestBook.side === 'Over' ? 'o' : 'u'}
          {bestBook.line.toFixed(1)} {bestBook.americanOdds}
        </Typography>
        <SportsbookLogo book={bestBook.book} bookIcon={bestBook.bookIcon} size={20} borderRadius="4px" />
      </Stack>

      {/* True Odds */}
      <Typography variant="body2" sx={{ color: '#f2f2f3b2', textAlign: 'center' }}>
        {bestBook.marketWin ? (
          bestBook.marketWin >= 0.5
            ? Math.round(-100 * bestBook.marketWin / (1 - bestBook.marketWin))
            : `+${Math.round(100 * (1 - bestBook.marketWin) / bestBook.marketWin)}`
        ) : '-'}
      </Typography>

      {/* Book Win % */}
      <Typography variant="body2" sx={{ color: '#f2f2f3b2', textAlign: 'center' }}>
        {bestBook.bookWin ? `${(bestBook.bookWin * 100).toFixed(0)}%` : '-'}
      </Typography>

      {/* Model Win % */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {isEVLoading ? (
          <CircularProgress size={16} sx={{ color: '#42A5F5' }} />
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#66BB6A', textAlign: 'center' }}>
            {displayModelWin ? `${(displayModelWin * 100).toFixed(0)}%` : '-'}
          </Typography>
        )}
      </Box>

      {/* EV % */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {isEVLoading ? (
          <CircularProgress size={16} sx={{ color: '#42A5F5' }} />
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: displayEV !== null && displayEV >= 0 ? '#66BB6A' : '#EF5350',
              textAlign: 'center',
            }}
          >
            {displayEV !== null
              ? `${displayEV >= 0 ? '+' : ''}${displayEV.toFixed(1)}%`
              : '-'}
          </Typography>
        )}
      </Box>

      {/* Other Books */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          alignItems: 'center',
          overflowX: 'auto',
          overflowY: 'hidden',
          whiteSpace: 'nowrap',
          width: '100%',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {otherBooks.length === 0 ? (
          <Typography variant="caption" color="text.secondary">-</Typography>
        ) : (
          otherBooks.map((book, index) => {
            // Get custom EV for this specific sportsbook if available
            const bookCustomEV = isCustomEV
              ? getCustomEV(prop.stat, book.side, book.book, customEVData)
              : null;
            const bookCustomModelWin = isCustomEV
              ? getCustomModelWin(prop.stat, book.side, book.book, customEVData)
              : null;

            return (
            <Stack
              key={`${book.book}-${index}`}
              direction="row"
              alignItems="center"
              spacing={0.5}
              onClick={(e) => {
                e.stopPropagation();
                onAddToBetSlip(book, {
                  projection: displayProjection,
                  evPct: bookCustomEV ?? book.evPct,
                  modelWin: bookCustomModelWin ?? book.modelWin,
                });
              }}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '4px',
                px: 1,
                py: 0.25,
                flexShrink: 0,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <SportsbookLogo book={book.book} bookIcon={book.bookIcon} size={18} borderRadius="3px" />
              <Typography variant="caption" color="text.primary">
                {book.side === 'Over' ? 'o' : 'u'}{book.line.toFixed(1)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({book.americanOdds})
              </Typography>
            </Stack>
            );
          })
        )}
      </Box>

      {/* Action Buttons */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="text"
          size="small"
          onClick={() => {
            if (isInBetSlip) {
              onRemoveFromBetSlip(betId);
            } else {
              onAddToBetSlip(bestBook, {
                projection: displayProjection,
                evPct: displayEV,
                modelWin: displayModelWin,
              });
            }
          }}
          startIcon={isInBetSlip ? <Minus size={14} weight="bold" /> : <Plus size={14} weight="bold" />}
          sx={{
            color: isInBetSlip ? '#EF5350' : '#42A5F5',
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'none',
            px: 1,
            py: 0.5,
            width: '88px',
            '&:hover': {
              backgroundColor: isInBetSlip ? 'rgba(239, 83, 80, 0.08)' : 'rgba(66, 165, 245, 0.08)',
            },
          }}
        >
          {isInBetSlip ? 'Remove' : 'Add'}
        </Button>
        <Button
          variant="outlined"
          color="success"
          size="small"
          disabled={!bestBook.deepLinkUrl}
          onClick={() => {
            if (bestBook.deepLinkUrl) {
              window.open(bestBook.deepLinkUrl, '_blank');
            }
          }}
          sx={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'none',
            px: 1.5,
            py: 0.5,
            minWidth: 'auto',
          }}
        >
          Bet
        </Button>
      </Stack>
    </Box>
  );
};

const StatsTable: React.FC<StatsTableProps> = ({
  player,
  game,
  customProjections,
  customEVData,
  isCustomEV,
  isEVLoading,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State for Other Books bottom sheet
  const [otherBooksOpen, setOtherBooksOpen] = useState(false);
  const [selectedProp, setSelectedProp] = useState<PlayerProp | null>(null);
  const betSlip = useSelector((state: IRootState) => state.stokasticProps.betSlip);

  const handleOpenOtherBooks = (prop: PlayerProp) => {
    setSelectedProp(prop);
    setOtherBooksOpen(true);
  };

  const handleCloseOtherBooks = () => {
    setOtherBooksOpen(false);
    setSelectedProp(null);
  };

  const handleAddToBetSlip = (
    prop: PlayerProp,
    sportsbook: PlayerPropSportsbook,
    customValues?: {
      projection: number;
      evPct: number | null;
      modelWin: number | null;
    }
  ) => {
    // Use custom values when available (from player customizations), otherwise use original
    const projection = customValues?.projection ?? prop.projection;
    const evPct = customValues?.evPct ?? sportsbook.evPct;
    const modelWin = customValues?.modelWin ?? sportsbook.modelWin;

    const betSlipItem: BetSlipItem = {
      id: `${player.id}-${prop.stat}-${sportsbook.book}-${sportsbook.side}`,
      playerName: player.name,
      avatar: player.avatar,
      stat: prop.stat,
      projection: projection.toFixed(1),
      bookLine: sportsbook.line.toString(),
      edge: sportsbook.delta.toFixed(1),
      bookWin: sportsbook.bookWin,
      modelWin: modelWin,
      evPct: evPct,
      book: sportsbook.book,
      bookLogo: sportsbook.bookIcon,
      odds: sportsbook.americanOdds,
      side: sportsbook.side,
      deepLinkUrl: sportsbook.deepLinkUrl,
    };
    dispatch(addToBetSlip(betSlipItem));
  };

  const handleRemoveFromBetSlip = (betId: string) => {
    const index = betSlip.findIndex((bet) => bet.id === betId);
    if (index !== -1) {
      dispatch(removeFromBetSlip(index));
    }
  };

  // Filter props to only show those with at least one sportsbook
  // (sportsbooks without true odds are already filtered out in transformPropsData.ts)
  // Then sort by EV% (highest first) - uses custom EV when available
  const sortedProps = useMemo(() => {
    // Helper to get the best EV for a prop (custom or original)
    const getPropBestEV = (prop: PlayerProp): number => {
      const bestBook = prop.sportsbooks[0];
      if (!bestBook) return -Infinity;

      // Check for custom EV if available
      if (isCustomEV && customEVData?.outcomes) {
        const customEV = getCustomEV(prop.stat, bestBook.side, bestBook.book, customEVData);
        if (customEV !== null) {
          return customEV;
        }
      }

      // Fall back to original EV
      return bestBook.evPct ?? -Infinity;
    };

    return [...player.props]
      .filter((prop) => prop.sportsbooks.length > 0)
      .filter((prop) => !HIDDEN_STATS.has(prop.stat))
      .sort((a, b) => getPropBestEV(b) - getPropBestEV(a));
  }, [player.props, customEVData, isCustomEV]);

  // Mobile layout
  if (isMobile) {
    // NEW: Vertical card layout (toggle USE_VERTICAL_MOBILE_LAYOUT to revert)
    if (USE_VERTICAL_MOBILE_LAYOUT) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {sortedProps.length > 0 ? (
            sortedProps.map((prop, index) => (
              <MobilePropRow
                key={`${prop.stat}-${prop.sportsbooks[0]?.side || 'unknown'}`}
                prop={prop}
                playerId={player.id}
                customProjection={getCustomProjection(prop.stat, customProjections)}
                customEVData={customEVData}
                isCustomEV={isCustomEV}
                isEVLoading={isEVLoading}
                betSlip={betSlip}
                onAddToBetSlip={(sportsbook, customValues) => handleAddToBetSlip(prop, sportsbook, customValues)}
                onRemoveFromBetSlip={handleRemoveFromBetSlip}
                onOpenOtherBooks={() => handleOpenOtherBooks(prop)}
                isLast={index === sortedProps.length - 1}
              />
            ))
          ) : (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No props available for this player.
              </Typography>
            </Box>
          )}

          {/* Other Books Bottom Sheet */}
          {selectedProp && (
            <OtherBooksBottomSheet
              open={otherBooksOpen}
              onClose={handleCloseOtherBooks}
              playerName={player.name}
              playerId={player.id}
              avatar={player.avatar}
              stat={selectedProp.stat}
              side={selectedProp.sportsbooks[0]?.side || 'Over'}
              projection={selectedProp.projection}
              sportsbooks={selectedProp.sportsbooks}
              onAddToBetSlip={(bet) => dispatch(addToBetSlip(bet))}
              betSlip={betSlip}
            />
          )}
        </Box>
      );
    }

    // OLD: Horizontally scrollable table layout
    return (
      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          mx: -2, // Negative margin to extend to card edges
          px: 2, // Padding to restore content position
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Box sx={{ minWidth: 'max-content' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0,
              pb: 1,
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            }}
          >
            <Typography sx={{ color: '#f2f2f3b2', fontWeight: 600, fontSize: '11.11px', width: 50, flexShrink: 0 }}>
              Stat
            </Typography>
            <Typography sx={{ color: '#f2f2f3b2', fontWeight: 600, fontSize: '11.11px', width: 70, flexShrink: 0, textAlign: 'center' }}>
              Projection
            </Typography>
            <Typography sx={{ color: '#f2f2f3b2', fontWeight: 600, fontSize: '11.11px', width: 100, flexShrink: 0 }}>
              Best Line
            </Typography>
            <Typography sx={{ color: '#f2f2f3b2', fontWeight: 600, fontSize: '11.11px', width: 60, flexShrink: 0, textAlign: 'center' }}>
              True Odds
            </Typography>
            <Typography sx={{ color: '#f2f2f3b2', fontWeight: 600, fontSize: '11.11px', width: 60, flexShrink: 0, textAlign: 'center' }}>
              Book Win %
            </Typography>
            <Typography sx={{ color: '#f2f2f3b2', fontWeight: 600, fontSize: '11.11px', width: 70, flexShrink: 0, textAlign: 'center' }}>
              Model Win %
            </Typography>
            <Typography sx={{ color: '#f2f2f3b2', fontWeight: 600, fontSize: '11.11px', width: 60, flexShrink: 0, textAlign: 'center' }}>
              EV %
            </Typography>
            <Typography sx={{ color: '#f2f2f3b2', fontWeight: 600, fontSize: '11.11px', width: 80, flexShrink: 0, textAlign: 'center' }}>
              Other Books
            </Typography>
            <Box sx={{ width: 100, flexShrink: 0 }} /> {/* Spacer for action buttons */}
          </Box>

          {/* Rows */}
          {sortedProps.length > 0 ? (
            sortedProps.map((prop) => (
              <PropRow
                key={`${prop.stat}-${prop.sportsbooks[0]?.side || 'unknown'}`}
                prop={prop}
                player={player}
                customProjection={getCustomProjection(prop.stat, customProjections)}
                customEVData={customEVData}
                isCustomEV={isCustomEV}
                isEVLoading={isEVLoading}
                betSlip={betSlip}
                onAddToBetSlip={(sportsbook, customValues) => handleAddToBetSlip(prop, sportsbook, customValues)}
                onRemoveFromBetSlip={handleRemoveFromBetSlip}
                onOpenOtherBooks={() => handleOpenOtherBooks(prop)}
              />
            ))
          ) : (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No props available for this player.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Other Books Bottom Sheet */}
        {selectedProp && (
          <OtherBooksBottomSheet
            open={otherBooksOpen}
            onClose={handleCloseOtherBooks}
            playerName={player.name}
            playerId={player.id}
            avatar={player.avatar}
            stat={selectedProp.stat}
            side={selectedProp.sportsbooks[0]?.side || 'Over'}
            projection={selectedProp.projection}
            sportsbooks={selectedProp.sportsbooks}
            onAddToBetSlip={(bet) => dispatch(addToBetSlip(bet))}
            betSlip={betSlip}
          />
        )}
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '70px 70px 130px 120px 100px 110px 80px 1fr 150px',
          alignItems: 'center',
          pb: 1,
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <Typography
          sx={{ color: '#f2f2f3b2', fontWeight: 600, fontSize: 13 }}
        >
          Stat
        </Typography>
        <Typography
          sx={{ color: '#f2f2f3b2', fontWeight: 600, fontSize: 13, textAlign: 'center' }}
        >
          Projection
        </Typography>
        <Typography
          sx={{ color: '#f2f2f3b2', fontWeight: 600, fontSize: 13 }}
        >
          Best Line
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: '#f2f2f3b2', fontWeight: 600, fontSize: 13, textAlign: 'center' }}
        >
          True Odds
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: '#f2f2f3b2', fontWeight: 600, fontSize: 13, textAlign: 'center' }}
        >
          Book Win %
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: '#f2f2f3b2', fontWeight: 600, fontSize: 13, textAlign: 'center' }}
        >
          Model Win %
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: '#f2f2f3b2', fontWeight: 600, fontSize: 13, textAlign: 'center' }}
        >
          EV %
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: '#f2f2f3b2', fontWeight: 600, fontSize: 13 }}
        >
          Other Books
        </Typography>
        <Box /> {/* Spacer for action buttons */}
      </Box>

      {/* Rows */}
      {sortedProps.length > 0 ? (
        sortedProps.map((prop) => (
          <PropRow
            key={`${prop.stat}-${prop.sportsbooks[0]?.side || 'unknown'}`}
            prop={prop}
            player={player}
            customProjection={getCustomProjection(prop.stat, customProjections)}
            customEVData={customEVData}
            isCustomEV={isCustomEV}
            isEVLoading={isEVLoading}
            betSlip={betSlip}
            onAddToBetSlip={(sportsbook, customValues) => handleAddToBetSlip(prop, sportsbook, customValues)}
            onRemoveFromBetSlip={handleRemoveFromBetSlip}
          />
        ))
      ) : (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No props available for this player.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default memo(StatsTable);
